import { Smartphone, Brain, Cloud, Satellite, Bell, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import { motion } from "motion/react";
import tractorBg from '../assets/68e532ab6c35a82dc5edd82d1a41c0a88938edf8.png';
import appScreenshot from '../assets/android-image.avif';
import { useLanguage } from "../context/LanguageContext";

export function DigitalAgriculture() {
  const { t } = useLanguage();
 // Feature data for the app features grid 
  const appFeatures = [
    {
      icon: Brain,
      title: "AI Recommendations",
      description: "Get personalized crop suggestions and farming advice powered by artificial intelligence and machine learning."
    },
    {
      icon: TrendingUp,
      title: "Crop Schedule & Planning",
      description: "Plan your entire farming season with automated schedules, reminders, and best practice recommendations."
    },
    {
      icon: Bell,
      title: "Weather Alerts",
      description: "Real-time weather updates, rainfall predictions, and climate advisories for better farm management."
    },
    {
      icon: Satellite,
      title: "Satellite Monitoring",
      description: "Track crop health, growth patterns, and field conditions using advanced satellite imagery and analytics."
    },
    {
      icon: Cloud,
      title: "Cloud Data Sync",
      description: "Access your farm data anywhere, anytime with secure cloud storage and automatic synchronization."
    },
    {
      icon: Smartphone,
      title: "Offline Access",
      description: "Work without internet connectivity. All essential features available offline with data sync when online."
    }
  ];

  return (
  // The Digital Agriculture Section with premium design, animations, and app features grid
  <section className="py-32 px-6 lg:px-20 relative overflow-hidden">
      {/* Premium Background with Tractor Image */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${tractorBg})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#163832]/95 via-[#0B2B26]/92 to-[#051F20]/90"></div>
        </div>
      </div>

      {/* Animated Pattern Overlay */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 25px 25px, #C9A961 2%, transparent 0%)`,
          backgroundSize: '50px 50px'
        }}
        animate={{
          backgroundPosition: ['0px 0px', '50px 50px']
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block bg-white/10 backdrop-blur-xl px-6 py-3 rounded-full border border-[#C9A961]/30 mb-6 shadow-2xl"
          >
            <p className="text-[#C9A961] tracking-wider uppercase text-sm">Digital Solutions</p>
          </motion.div>
          <motion.h2 
            className="text-5xl lg:text-6xl text-white mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('digitalApp.title')}
          </motion.h2>
          <motion.p 
            className="text-white/90 text-lg max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('digitalApp.subtitle')}
          </motion.p>
        </div>

        {/* App Mockup & Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16">
          {/* Mobile Mockup with Frame */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: -50, rotateY: -20 }}
            whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#C9A961]/20 rounded-full blur-3xl"></div>
            
            {/* Mobile Phone Frame */}
            <motion.div 
              className="relative mx-auto max-w-sm"
              whileHover={{ scale: 1.02, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {/* Phone Border/Frame */}
              <div className="relative bg-gradient-to-br from-[#051F20] to-[#163832] rounded-[3rem] p-3 shadow-2xl">
                {/* Screen Notch Area */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-7 bg-[#051F20] rounded-b-3xl z-10"></div>
                
                {/* Screen Container */}
                <div className="relative bg-white rounded-[2.5rem] overflow-hidden">
                  {/* Status Bar */}
                  <div className="bg-white px-6 pt-8 pb-2 flex justify-between items-center">
                    <span className="text-xs">9:41</span>
                    <div className="flex gap-1">
                      <div className="w-4 h-3 bg-gray-400 rounded-sm"></div>
                      <div className="w-4 h-3 bg-gray-400 rounded-sm"></div>
                      <div className="w-4 h-3 bg-gray-400 rounded-sm"></div>
                    </div>
                  </div>
                  
                  {/* App Screenshot */}
                  <motion.img 
                    src={appScreenshot}
                    alt="Kalpakrushi Mobile App"
                    className="w-full h-auto"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  />
                  
                  {/* Bottom Home Indicator */}
                  <div className="bg-white pb-3 flex justify-center">
                    <div className="w-32 h-1 bg-gray-300 rounded-full"></div>
                  </div>
                </div>
                
                {/* Side Buttons */}
                <div className="absolute right-0 top-32 w-1 h-16 bg-[#163832] rounded-l"></div>
                <div className="absolute right-0 top-52 w-1 h-12 bg-[#163832] rounded-l"></div>
                <div className="absolute left-0 top-40 w-1 h-12 bg-[#163832] rounded-r"></div>
              </div>
              
              <div className="mt-6 text-center">
                <h3 className="text-white text-xl mb-2">Available on iOS & Android</h3>
                <p className="text-white/80 text-sm">Download from App Store or Play Store</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Features Grid - 3 Columns x 2 Rows */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {appFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08, duration: 0.5 }}
                whileHover={{ y: -8, scale: 1.05 }}
                className="group bg-white/10 backdrop-blur-xl p-5 rounded-2xl border border-white/20 hover:border-[#C9A961]/50 hover:bg-white/15 transition-all duration-300 shadow-xl"
              >
                <motion.div 
                  className="h-12 w-12 bg-gradient-to-br from-[#C9A961] to-[#8EB69B] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                  whileHover={{ scale: 1.15, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <feature.icon className="h-6 w-6 text-white" />
                </motion.div>
                <h4 className="text-white text-center mb-2 group-hover:text-[#C9A961] transition-colors">
                  {feature.title}
                </h4>
                <p className="text-white/70 text-sm text-center leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#235347] text-white px-12 py-7 text-lg shadow-2xl hover:shadow-[#C9A961]/50 transition-all duration-300 rounded-2xl border-2 border-white/20">
              Download Kalpakrushi App
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <motion.div 
        className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C9A961]/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </section>
  );
}